// frontend/src/api/tax.ts
//
// TAX-PARSE-2 — typed API client functions for tax document intake,
// 3-year cross-verification, and TaxEvidence → MIR surfacing.
//
// Mirrors the style of api/client.ts: thin wrappers around `apiClient`
// (baseURL = VITE_API_BASE_URL + '/api'), so every path below is relative
// to `/api`.

import { apiClient } from '@/api/client'

// ── Types ────────────────────────────────────────────────────────────────────

export interface TaxDocumentResponse {
  doc_id: string
  form_type: string
  tax_year: number | null
  fields: Record<string, unknown>
  confidence: number
  needs_verification: boolean
  source_document: string
}

export interface TaxDocumentSummary {
  doc_id: string
  form_type: string
  tax_year: number | null
  confidence: number
  needs_verification: boolean
}

export type CrossVerifyFlag = 'PASS' | 'REVIEW' | 'FLAG'

export interface CrossVerificationRow {
  year: number
  revenue_check: Record<string, unknown>
  overall_flag: CrossVerifyFlag
  needs_verification?: boolean
}

export interface TaxDiscrepancy {
  year: number
  field: string
  tax_value: number
  bank_value: number
  variance_abs: number
  variance_pct: number
  flag: string
}

export interface ProofOfCash {
  year: number
  tax_revenue: number
  bank_normalized_deposits: number
  variance_abs: number
  variance_pct: number
  flag: string
}

export interface CrossVerificationResult {
  deal_id: string
  years: number[]
  rows: CrossVerificationRow[]
  discrepancies: TaxDiscrepancy[]
  proof_of_cash: ProofOfCash
  overall_flag: CrossVerifyFlag
  summary: string
}

export interface TaxEvidenceItem {
  id: string
  field_name: string
  value: number
  confidence: number
  form_type: string
  tax_year: number | null
  flagged: boolean
  notes: string | null
}

export interface TaxEvidenceToMirResult {
  number: number
  score: string
  score_note: string
}

// ── API functions ────────────────────────────────────────────────────────────

export async function uploadTaxDocument(
  dealId: string,
  file: File,
  opts?: { formType?: string; taxYear?: string | number },
): Promise<TaxDocumentResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('deal_id', dealId)
  if (opts?.formType) form.append('form_type', opts.formType)
  if (opts?.taxYear != null) form.append('tax_year', String(opts.taxYear))

  const res = await apiClient.post<TaxDocumentResponse>('/v1/tax/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function crossVerifyTax(
  dealId: string,
  docIds?: string[],
): Promise<CrossVerificationResult> {
  const res = await apiClient.post<CrossVerificationResult>('/v1/tax/cross-verify', {
    deal_id: dealId,
    ...(docIds ? { doc_ids: docIds } : {}),
  })
  return res.data
}

export async function listTaxDocuments(dealId: string): Promise<TaxDocumentSummary[]> {
  const res = await apiClient.get<TaxDocumentSummary[]>(
    `/v1/tax/documents/${encodeURIComponent(dealId)}`,
  )
  return res.data
}

export async function taxEvidenceToMir(
  evidenceId: string,
  dealId: string,
  findingNumber?: number,
): Promise<TaxEvidenceToMirResult> {
  const res = await apiClient.patch<TaxEvidenceToMirResult>(
    `/v1/tax/evidence/${encodeURIComponent(evidenceId)}/to-mir`,
    {
      deal_id: dealId,
      ...(findingNumber != null ? { finding_number: findingNumber } : {}),
    },
  )
  return res.data
}

export async function getTaxEvidence(dealId: string): Promise<TaxEvidenceItem[]> {
  const res = await apiClient.get<TaxEvidenceItem[]>(
    `/v1/tax/evidence/${encodeURIComponent(dealId)}`,
  )
  return res.data
}
